// Carte « origine probable de l'eau potable » : port du dessin SVG du POC (template_carte.html).
// rankSources/autoTexts font le même calcul que rank_sources/auto_texts dans api/water_origin.py :
// garder les deux en phase.

// format fixe A4 paysage (VIEW_RATIO dans water_origin.py) : le contenu est centré et l'emprise
// élargie dans l'autre sens, ce qui fait apparaître les départements voisins
export const W = 1000;
export const H = 707;
const PAD = 24;
const NS = "http://www.w3.org/2000/svg";
const C = { ink: "#1f2a4d", surf: "#7b4f6b", sout: "#173a2f", unk: "#a29aa0", river: "#b7d9d3", panel: "#f3f1f2", nb: "#f1eef0" };
const FONT = "Fira Sans, Segoe UI, Helvetica, Arial, sans-serif";
const FONTC = "Fira Sans Condensed, Fira Sans, Arial Narrow, sans-serif";

export const TYPE_COLORS = { surface: C.surf, souterrain: C.sout, inconnu: C.unk };
export const TYPE_LABELS = { surface: "Surface", souterrain: "Souterrain", inconnu: "Indéterminé" };
// curseurs d'affichage : classe de cours d'eau maximale (river_class) et volume minimal des points (m³/an)
export const RIV_LIB = ["masqués", "fleuves seulement (plus de 200 km)", "plus de 80 km", "plus de 35 km", "plus de 15 km", "tous (plus de 5 km)"];
export const VMIN = [0, 1e4, 2.5e4, 5e4, 1e5, 2.5e5, 5e5, 1e6, 2.5e6, 5e6];
export const DEFAULT_SETTINGS = { rayon: 15, pos: "bl", labels: true, box: false, riv: 5, vmin: 0, vminOut: false, legend: true };
export const INTRO = "Grâce à l'analyse des données provenant de la BNPE, on peut imaginer que l'eau potable sur votre site provient de :";
const NONE_FOUND = "aucune ressource identifiée";

const fmt = n => Math.round(n).toLocaleString("fr-FR");
export const fmtVol = v => v >= 1e6 ? (v / 1e6).toLocaleString("fr-FR", { maximumFractionDigits: 1 }) + " Mm³" : fmt(v) + " m³";
const nk = s => s.normalize("NFD").replace(/\p{Mn}/gu, "").toLowerCase();

export function km([lo1, la1], [lo2, la2]) {
  const r = Math.PI / 180;
  const a = Math.sin((la2 - la1) * r / 2) ** 2 + Math.cos(la1 * r) * Math.cos(la2 * r) * Math.sin((lo2 - lo1) * r / 2) ** 2;
  return 2 * 6371 * Math.asin(Math.sqrt(a));
}

function inPoly(pt, g) {
  if (!g) return false;
  const polys = g.type === "Polygon" ? [g.coordinates] : g.type === "MultiPolygon" ? g.coordinates : [];
  return polys.some(poly => {
    let ins = false; const r = poly[0];
    for (let i = 0, j = r.length - 1; i < r.length; j = i++) {
      const [xi, yi] = r[i], [xj, yj] = r[j];
      if (((yi > pt[1]) !== (yj > pt[1])) && (pt[0] < (xj - xi) * (pt[1] - yi) / (yj - yi) + xi)) ins = !ins;
    }
    return ins;
  });
}

// modèle gravitaire : gros volume et proximité ; une ressource dans la commune compte comme à 1 km au plus
export function rankSources(origin, rayon) {
  const site = origin.site, c = [site.lon, site.lat];
  const list = origin.ouvrages.filter(o => o.vol > 0).map(o => {
    const inside = o.insee === site.insee || inPoly([o.lon, o.lat], site.g);
    const d = km(c, [o.lon, o.lat]);
    return { ...o, d, inside, score: o.vol / Math.pow((inside ? Math.min(d, 1) : d) + 1, 2) };
  }).filter(o => o.d <= rayon || o.inside).sort((a, b) => b.score - a.score);
  const best = t => list.find(o => o.type === t);
  return { list, surf: best("surface"), sout: best("souterrain") };
}

export function autoTexts(a) {
  const g = !a.sout ? NONE_FOUND : nk(a.sout.label).includes(nk(a.sout.commune)) ? a.sout.label : `${a.sout.label} (${a.sout.commune})`;
  return { intro: INTRO, surf: a.surf ? (a.surf.riviere || a.surf.label) : NONE_FOUND, sout: g };
}

const walk = (co, f) => typeof co[0] === "number" ? f(co) : co.forEach(c => walk(c, f));
function geomBox(g) {
  if (!g._bb) {
    const b = [Infinity, Infinity, -Infinity, -Infinity];
    walk(g.coordinates, ([x, y]) => { b[0] = Math.min(b[0], x); b[1] = Math.min(b[1], y); b[2] = Math.max(b[2], x); b[3] = Math.max(b[3], y); });
    Object.defineProperty(g, "_bb", { value: b, enumerable: false });
  }
  return g._bb;
}
const bbOverlap = (a, b) => !(a[2] < b[0] || a[0] > b[2] || a[3] < b[1] || a[1] > b[3]);

function el(tag, attrs = {}, parent) {
  const e = document.createElementNS(NS, tag);
  for (const k in attrs) e.setAttribute(k, attrs[k]);
  if (parent) parent.appendChild(e);
  return e;
}
function text(parent, x, y, s, a = {}) {
  const t = el("text", { x, y, "font-family": FONT, "font-size": 12, fill: C.ink, ...a }, parent);
  t.textContent = s;
  return t;
}
function wrap(s, maxChars) {
  const out = []; let cur = "";
  s.split(/\s+/).forEach(w => { if ((cur + " " + w).trim().length > maxChars) { if (cur) out.push(cur); cur = w; } else cur = (cur + " " + w).trim(); });
  if (cur) out.push(cur);
  return out;
}
const tw = (s, size) => s.length * size * 0.53; // largeur approximative

// trait des cours d'eau selon leur classe de longueur (1 = grand fleuve … 5 = plus de 5 km) ;
// les grands axes sont tracés en dernier pour passer au-dessus du chevelu
const RIV = { 1: [2.6, "#6aa9c4"], 2: [2.0, "#6aa9c4"], 3: [1.5, "#7fb6cc"], 4: [1.1, "#93c2d3"], 5: [0.7, C.river], 6: [0.5, C.river] };

function calloutBox(pos) {
  const w = 390, h = 168, m = 14;
  return { w, h, x: pos.endsWith("l") ? m : W - w - m, y: pos.startsWith("b") ? H - h - m : m + (pos === "tl" ? 72 : 0) };
}

// Dessine la carte dans `svg` (vidé d'abord). `analysis` = rankSources(origin, settings.rayon),
// `texts` = textes de l'encart (corrigés sinon automatiques).
export function drawMap(svg, { origin, layers, settings: state, analysis: a, texts }) {
  const site = origin.site, c = [site.lon, site.lat];
  const deptGeoms = [layers.dept.g];
  const commB = geomBox(layers.dept.g);

  // emprise = département + cercle de recherche s'il en sort (ressources des départements voisins)
  const dlat = state.rayon / 110.57 * 1.08, dlon = state.rayon / (111.32 * Math.cos(c[1] * Math.PI / 180)) * 1.08;
  const b = [Math.min(commB[0], c[0] - dlon), Math.min(commB[1], c[1] - dlat), Math.max(commB[2], c[0] + dlon), Math.max(commB[3], c[1] + dlat)];
  const kx = Math.cos((b[1] + b[3]) / 2 * Math.PI / 180);
  const sc = Math.min((W - 2 * PAD) / ((b[2] - b[0]) * kx), (H - 2 * PAD) / (b[3] - b[1]));
  const mx = (b[0] + b[2]) / 2, my = (b[1] + b[3]) / 2, hw = (W - 2 * PAD) / (2 * sc * kx), hh = (H - 2 * PAD) / (2 * sc);
  const allB = [mx - hw, my - hh, mx + hw, my + hh];
  const P = ([x, y]) => [PAD + (x - allB[0]) * kx * sc, PAD + (allB[3] - y) * sc];
  const ring = r => r.map((p, i) => (i ? "L" : "M") + P(p).map(v => v.toFixed(1)).join(",")).join("") + "Z";
  const line = l => l.map((p, i) => (i ? "L" : "M") + P(p).map(v => v.toFixed(1)).join(",")).join("");
  const gpath = g => {
    if (!g) return "";
    if (g.type === "Polygon") return g.coordinates.map(ring).join("");
    if (g.type === "MultiPolygon") return g.coordinates.map(p => p.map(ring).join("")).join("");
    if (g.type === "LineString") return line(g.coordinates);
    if (g.type === "MultiLineString") return g.coordinates.map(line).join("");
    return "";
  };
  const drawRivers = (list, g) => {
    [...list].filter(f => (f.i || 6) <= state.riv).sort((x, y) => (y.i || 6) - (x.i || 6)).forEach(f => {
      if (!bbOverlap(geomBox(f.g), allB)) return;
      const [w, col] = RIV[f.i] || RIV[6];
      el("path", { d: gpath(f.g), stroke: col, "stroke-width": w }, g);
    });
  };

  svg.innerHTML = "";
  svg.setAttribute("viewBox", `0 0 ${W} ${H}`);
  el("rect", { x: 0, y: 0, width: W, height: H, fill: "#ffffff" }, svg);

  // départements voisins (visibles quand le cercle déborde) : trait épais dessous, aplat dessus,
  // un département après l'autre pour que les limites entre voisins restent tracées
  const voisins = (layers.voisins || []).filter(v => bbOverlap(geomBox(v.g), allB));
  voisins.forEach(v => {
    const d = gpath(v.g);
    el("path", { d, fill: C.nb, stroke: "#c3bcc1", "stroke-width": 1.6, "stroke-linejoin": "round" }, svg);
    el("path", { d, fill: C.nb, stroke: C.nb, "stroke-width": 0.6, "stroke-linejoin": "round" }, svg);
  });
  const gro = el("g", { fill: "none", "stroke-linecap": "round", "stroke-linejoin": "round" }, svg);
  drawRivers(layers.rivers_out || [], gro);

  // département du site : même principe, cours d'eau découpés sur son contour
  const deptD = deptGeoms.map(gpath).join("");
  const defs = el("defs", {}, svg);
  const cp = el("clipPath", { id: "clipDept" }, defs); el("path", { d: deptD }, cp);
  el("path", { d: deptD, fill: "#ffffff", stroke: "#9aa0ad", "stroke-width": 2.2, "stroke-linejoin": "round" }, svg);
  el("path", { d: deptD, fill: "#ffffff", stroke: "#ffffff", "stroke-width": 0.6, "stroke-linejoin": "round" }, svg);
  const gr = el("g", { "clip-path": "url(#clipDept)", fill: "none", "stroke-linecap": "round", "stroke-linejoin": "round" }, svg);
  drawRivers(layers.rivers || [], gr);

  // commune du site et rayon
  el("path", { d: gpath(site.g), fill: "#dfe3f0", stroke: C.ink, "stroke-width": 1.4, "stroke-linejoin": "round" }, svg);
  const [cx, cy] = P(c);
  el("circle", { cx, cy, r: state.rayon * sc / 111.2, fill: "none", stroke: C.ink, "stroke-width": 0.8, "stroke-dasharray": "4 4", opacity: 0.45 }, svg);

  // ouvrages
  const maxV = Math.max(...origin.ouvrages.map(o => o.vol), 1);
  const R = v => 2.6 + 8.5 * Math.sqrt(v / maxV);
  const col = t => TYPE_COLORS[t] || C.unk;
  const isPick = q => (a.surf && q.code === a.surf.code) || (a.sout && q.code === a.sout.code);
  const inRay = new Set(a.list.map(o => o.code));
  // curseur « points de captage » : les petits volumes sont masqués, sauf les ressources retenues ;
  // option « seulement hors du cercle » : les captages du rayon (ou de la commune) restent tous affichés
  const shown = o => o.vol > 0 && (o.vol >= VMIN[state.vmin] || isPick(o) || (state.vminOut && inRay.has(o.code)));
  // plusieurs ouvrages au même endroit (compteurs d'une même station, coordonnées au centre de la
  // commune) : un seul point, le plus gros, cerclé si l'un d'eux est dans le rayon, doublement si retenu
  const gp = el("g", {}, svg);
  const spots = new Map();
  [...origin.ouvrages].filter(shown).sort((x, y) => y.vol - x.vol).forEach(o => {
    const k = P([o.lon, o.lat]).map(v => Math.round(v / 2)).join(",");
    const s = spots.get(k); if (s) s.all.push(o); else spots.set(k, { o, all: [o] });
  });
  [...spots.values()].forEach(({ o, all }) => {
    const [x, y] = P([o.lon, o.lat]);
    const pick = all.some(isPick), ray = all.some(q => inRay.has(q.code));
    const t = (all.find(isPick) || all.find(q => q.type !== "inconnu") || o).type;
    const dot = el("circle", { cx: x.toFixed(1), cy: y.toFixed(1), r: R(o.vol).toFixed(1), fill: col(t), "fill-opacity": t === "inconnu" ? 0.55 : 0.92, stroke: "#ffffff", "stroke-width": 0.6 }, gp);
    if (ray) el("circle", { cx: x.toFixed(1), cy: y.toFixed(1), r: (R(o.vol) + 3).toFixed(1), fill: "none", stroke: col(t), "stroke-width": 1.2 }, gp);
    if (pick) el("circle", { cx: x.toFixed(1), cy: y.toFixed(1), r: (R(o.vol) + 6).toFixed(1), fill: "none", stroke: col(t), "stroke-width": 1.6 }, gp);
    el("title", {}, dot).textContent = all.map(q => `${q.nom} — ${q.commune}\n${q.type} · ${fmtVol(q.vol)}/an`).join("\n\n");
  });

  // étiquettes, en évitant légende, encart, repère du site et noms déjà posés
  const placed = [];
  const collide = bx => placed.some(p => !(bx[2] < p[0] || bx[0] > p[2] || bx[3] < p[1] || bx[1] > p[3]));
  const box = calloutBox(state.pos);
  if (state.legend) placed.push([8, 8, 224, state.vmin ? 147 : 130]);
  if (state.box) placed.push([box.x - 4, box.y - 4, box.x + box.w + 4, box.y + box.h + 4]);
  const label = o => {
    const [x, y] = P([o.lon, o.lat]); const r = R(o.vol) + 2; const s = o.label; const w = tw(s, 10.5), h = 11;
    for (const [lx, ly] of [[x - w / 2, y - r - 3], [x - w / 2, y + r + h], [x + r + 2, y + 4], [x - r - 2 - w, y + 4]]) {
      const bx = [lx - 1, ly - h, lx + w + 1, ly + 2];
      if (!collide(bx)) {
        placed.push(bx);
        text(svg, lx.toFixed(1), ly.toFixed(1), s, { "font-family": FONTC, "font-size": 10.5, fill: "#2b2b33", stroke: "#ffffff", "stroke-width": 2.6, "paint-order": "stroke", "stroke-linejoin": "round" });
        return;
      }
    }
  };
  placed.push([cx - 10, cy - 30, cx + 10, cy]);
  placed.push([cx + 12, cy - 12, cx + 12 + tw("Votre site", 13) + 4, cy + 4]);
  // nom des départements voisins : dans leur partie visible
  voisins.forEach(v => {
    const s = `${v.nom} (${v.code})`.toUpperCase(), w = tw(s, 11) * 1.12, h = 12;
    const vb = geomBox(v.g), hits = [], step = 22;
    for (let py = PAD + h; py < H - PAD; py += step) for (let px = PAD + w / 2; px < W - PAD - w / 2; px += step) {
      const ll = [allB[0] + (px - PAD) / (kx * sc), allB[3] - (py - PAD) / sc];
      if (ll[0] < vb[0] || ll[0] > vb[2] || ll[1] < vb[1] || ll[1] > vb[3] || !inPoly(ll, v.g)) continue;
      if (collide([px - w / 2 - 2, py - h, px + w / 2 + 2, py + 3])) continue;
      hits.push([px, py]);
    }
    if (hits.length < 4) return; // trop peu visible pour être nommé
    const hx = hits.reduce((t, p) => t + p[0], 0) / hits.length, hy = hits.reduce((t, p) => t + p[1], 0) / hits.length;
    const [px, py] = hits.reduce((bp, p) => Math.hypot(p[0] - hx, p[1] - hy) < Math.hypot(bp[0] - hx, bp[1] - hy) ? p : bp);
    placed.push([px - w / 2 - 2, py - h, px + w / 2 + 2, py + 3]);
    text(svg, px.toFixed(1), py.toFixed(1), s, { "text-anchor": "middle", "font-size": 11, "letter-spacing": 1.2, fill: "#8d868b", stroke: C.nb, "stroke-width": 2.5, "paint-order": "stroke" });
  });
  if (state.labels) {
    const seen = new Set();
    [a.surf, a.sout].filter(Boolean).forEach(o => { seen.add(o.label); label(o); });
    [...origin.ouvrages].filter(shown).sort((x, y) => y.vol - x.vol).forEach(o => {
      if (seen.size >= 14 || seen.has(o.label)) return;
      seen.add(o.label); label(o);
    });
  }

  // repère du site
  el("path", { d: `M${cx},${cy} c-2.2,-5.5 -8.5,-10.5 -8.5,-16.5 a8.5,8.5 0 1 1 17,0 c0,6 -6.3,11 -8.5,16.5z`, fill: C.ink, stroke: "#ffffff", "stroke-width": 1.2 }, svg);
  el("circle", { cx, cy: cy - 16.5, r: 3.2, fill: "#ffffff" }, svg);
  text(svg, cx + 12, cy + 1, "Votre site", { "font-size": 13, "font-weight": 600, fill: C.ink, stroke: "#ffffff", "stroke-width": 3, "paint-order": "stroke" });

  if (state.legend) drawLegend(svg, origin, state);
  if (state.box) drawCallout(svg, texts, box, cx, cy);
}

function drawLegend(svg, origin, state) {
  const lg = el("g", { transform: "translate(14,14)" }, svg);
  const items = [["Prélèvement souterrain", C.sout, 0.92], ["Prélèvement de surface", C.surf, 0.92]];
  if (origin.ouvrages.some(o => o.type === "inconnu" && o.vol > 0)) items.push(["Milieu non déterminé", C.unk, 0.55]);
  el("rect", { x: 0, y: 0, width: 206, height: 26 + (items.length + 2) * 17 + (state.vmin ? 17 : 0), fill: "#ffffff", "fill-opacity": 0.85 }, lg);
  text(lg, 4, 15, "Sites BNPE", { "font-size": 13, "font-weight": 500, fill: "#3a3a44" });
  items.forEach(([s, c, op], i) => {
    el("circle", { cx: 10, cy: 32 + i * 17, r: 4.5, fill: c, "fill-opacity": op }, lg);
    text(lg, 22, 36 + i * 17, s, { "font-size": 11.5, fill: "#3a3a44" });
  });
  const ly = 32 + items.length * 17;
  el("circle", { cx: 10, cy: ly, r: 3.5, fill: "#55505a" }, lg);
  el("circle", { cx: 10, cy: ly, r: 6.5, fill: "none", stroke: "#55505a", "stroke-width": 1.2 }, lg);
  text(lg, 22, ly + 4, `Ressource dans le rayon (${state.rayon} km)`, { "font-size": 11.5, fill: "#3a3a44" });
  el("circle", { cx: 10, cy: ly + 17, r: 3.5, fill: "#55505a" }, lg);
  el("circle", { cx: 10, cy: ly + 17, r: 6.5, fill: "none", stroke: "#55505a", "stroke-width": 1.2 }, lg);
  el("circle", { cx: 10, cy: ly + 17, r: 9, fill: "none", stroke: "#55505a", "stroke-width": 1.4 }, lg);
  text(lg, 22, ly + 21, state.box ? "Ressource retenue dans l'encart" : "Ressource la plus probable", { "font-size": 11.5, fill: "#3a3a44" });
  if (state.vmin) text(lg, 4, ly + 40, state.vminOut ? `Hors du cercle : < ${fmtVol(VMIN[state.vmin])}/an masqués` : `Prélèvements < ${fmtVol(VMIN[state.vmin])}/an non affichés`, { "font-size": 10.5, fill: "#6c6570", "font-style": "italic" });
}

function drawCallout(svg, texts, b, cx, cy) {
  const g = el("g", {}, svg);
  const conn = el("polyline", { fill: "none", stroke: C.ink, "stroke-width": 1.3 }, g);
  const rect = el("rect", { x: b.x, y: b.y, width: b.w, height: b.h, rx: 14, fill: C.panel, stroke: "#e2dde0", "stroke-width": 1 }, g);
  let y = b.y + 28;
  wrap(texts.intro, 52).forEach(l => { text(g, b.x + 16, y, l, { "font-size": 15, fill: "#1f1f27" }); y += 21; });
  y += 8;
  const line = (title, value, fill) => {
    const t = text(g, b.x + 16, y, "", { "font-size": 15.5, fill });
    el("tspan", { "font-weight": 700 }, t).textContent = title;
    el("tspan", {}, t).textContent = value;
  };
  line("Eaux superficielles : ", texts.surf, C.surf);
  y += 31;
  line("Eaux souterraines : ", texts.sout, C.ink);
  rect.setAttribute("height", y - b.y + 22);
  // élargit si une ligne déborde
  const longest = Math.max(tw("Eaux superficielles : " + texts.surf, 15.5), tw("Eaux souterraines : " + texts.sout, 15.5)) + 32;
  if (longest > b.w) { rect.setAttribute("width", longest); if (b.x > W / 2) rect.setAttribute("x", W - 14 - longest); }
  // connecteur coudé : bord de l'encart le plus proche → coude → site
  const rx = +rect.getAttribute("x"), rw = +rect.getAttribute("width"), ry = +rect.getAttribute("y"), rh = +rect.getAttribute("height");
  const left = rx + rw / 2 < cx;
  const sx = left ? rx + rw : rx;
  const sy = Math.max(ry + 18, Math.min(ry + rh - 18, cy));
  const ex = cx + (left ? -16 : 16);
  const mx = (sx + ex) / 2;
  conn.setAttribute("points", (left ? sx < ex : sx > ex) ? `${sx},${sy} ${mx},${sy} ${mx},${cy - 6} ${ex},${cy - 6}` : `${rx + rw / 2},${cy < ry ? ry : ry + rh} ${rx + rw / 2},${cy - 6} ${ex},${cy - 6}`);
}

// --- export -----------------------------------------------------------------------------------

// Une image SVG chargée dans <img> (export PNG) n'a pas accès aux polices de la page : on embarque
// Fira Sans / Fira Sans Condensed (sous-ensembles latins de Google Fonts) dans le SVG exporté.
const FONTS_CSS = "https://fonts.googleapis.com/css2?family=Fira+Sans:wght@400;500;600;700&family=Fira+Sans+Condensed:wght@400;500&display=swap";
let fontCssPromise = null;

function embeddedFontCss() {
  if (!fontCssPromise) {
    fontCssPromise = (async () => {
      const css = await (await fetch(FONTS_CSS)).text();
      const blocks = css.split(/(?=\/\* [\w-]+ \*\/)/).filter(bl => /^\/\* latin(-ext)? \*\//.test(bl));
      const toDataUrl = async url => {
        const blob = await (await fetch(url)).blob();
        return new Promise(resolve => { const r = new FileReader(); r.onload = () => resolve(r.result); r.readAsDataURL(blob); });
      };
      let out = blocks.join("\n");
      for (const url of [...new Set(out.match(/https:\/\/[^)]+\.woff2/g) || [])]) out = out.split(url).join(await toDataUrl(url));
      return out;
    })().catch(() => { fontCssPromise = null; return ""; });
  }
  return fontCssPromise;
}

export async function svgString(svg) {
  const copy = svg.cloneNode(true);
  copy.setAttribute("width", W);
  copy.setAttribute("height", H);
  const css = await embeddedFontCss();
  if (css) copy.insertBefore(el("style", {}), copy.firstChild).textContent = css;
  return '<?xml version="1.0" encoding="UTF-8"?>\n' + new XMLSerializer().serializeToString(copy);
}

export async function pngDataUrl(svg, scale = 2.5) {
  const source = await svgString(svg);
  const img = new Image();
  await new Promise((resolve, reject) => {
    img.onload = resolve;
    img.onerror = () => reject(new Error("Conversion de la carte en image impossible"));
    img.src = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(source);
  });
  const canvas = document.createElement("canvas");
  canvas.width = W * scale; canvas.height = H * scale;
  canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL("image/png");
}

export function download(href, name) {
  const link = document.createElement("a");
  link.href = href; link.download = name;
  document.body.appendChild(link); link.click(); link.remove();
}
